import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import { Button } from "@mui/joy";
import { Box, Divider, Paper, Tooltip, Typography } from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GridActionsCellItem,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  Toolbar,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { Dispatch } from "@reduxjs/toolkit";
import { DateRange } from "@mui/x-date-pickers-pro";
import { ConfirmOptions, useConfirm } from "material-ui-confirm";
import moment, { Moment } from "moment";
import { useEffect, useMemo, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch } from "react-redux";
import {
  currencyFormatter,
  defaultDataGridProps,
  injectDefaultColumnProps,
} from "../../constants";
import { Vtransaction_Entity } from "../../entities/vtransaction";

import {
  getterDivider,
  setterDivider,
} from "../jobCategory/jobCategoryRates/dataGridColDef";
import NoRowsOverlay from "../noRowsOverlay/noRowsOverlay";
import { usePendingEdits } from "../pendingEdits/pendingEdits";
import { useSaveError } from "../saveError/saveError";
import { useNavigate } from "react-router-dom";
import {
  summarizeEarnings,
  summarizeLedger,
  withinDateRange,
} from "../../helpers/talentEarnings";
import { useJobs } from "../../redux/job/jobHooks";
import { DateRangePicker } from "../dateRangePicker/dateRangePicker";
import { useVtransactions } from "../../redux/vtransaction/vtransactionHooks";
import {
  selectedProjectID,
  selectedTalentID,
  setSelectedProjectID,
  setSelectedTalentID,
  setSelectedVtransactionTalentID,
} from "../../redux/filmsReducer";
import { useSelector } from "react-redux";
import {
  useCreateVtransactionMutation,
  useDeleteVtransactionMutation,
  useUpdateVtransactionMutation,
} from "../../redux/vtransaction/vtransactionEndpoints";

/**
 * TalentLedger
 *
 * @returns {null} TalentLedger
 */
export default function TalentLedger({
  talentID,
  header = false,
  withSummary = false,
  dateRange,
}: {
  talentID?: number;
  header?: boolean;
  /**
   * Show the date range picker and the earnings cards above the grid. Off by
   * default: the ledger also renders as a narrow side panel next to Jobs and
   * inside a row's detail panel, where there is no room for either.
   */
  withSummary?: boolean;
  /**
   * Range to filter by, when something outside owns the picker — the main
   * ledger page does, and its detail panels have to agree with the totals on
   * the row above them.
   */
  dateRange?: DateRange<Moment>;
}) {
  const confirm = useConfirm();
  const _projectID = useSelector(selectedProjectID);
  const _talentID = talentID || useSelector(selectedTalentID);
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  // Open by default, so the ledger still opens on the talent's whole history
  // the way it always has. Narrowing it is the user's move.
  const [date, setDate] = useState<DateRange<Moment>>([null, null]);
  const [createVtransaction] = useCreateVtransactionMutation();
  const [updateVtransaction] = useUpdateVtransactionMutation();
  const [deleteVtransaction] = useDeleteVtransactionMutation();
  const askQuestion = useConfirm();
  const { vtransactions } = useVtransactions({
    filters: JSON.stringify([
      {
        name: "vtransaction.talentID",
        value: _talentID,
        comparison: "eq",
      },
    ]),
  });
  // Hours live on jobs, not on transactions, so the earnings cards read the
  // same source the Invoices tab does — identical filters, so RTK Query serves
  // both from one cache entry rather than fetching twice.
  const { jobs } = useJobs(
    {
      filters: JSON.stringify([
        { name: "jobs.talentID", value: _talentID, comparison: "eq" },
      ]),
    },
    { skip: !withSummary || !_talentID }
  );
  const { reportSaveError, saveErrorSnackbar } = useSaveError();
  const {
    pendingEdits,
    pendingValue,
    markPending,
    dropPending,
    releaseSettled,
  } = usePendingEdits();

  // Fresh server data: let go of the cells it has caught up with.
  useEffect(() => {
    releaseSettled((id, field) => {
      const vt = vtransactions?.find((v) => v.id === Number(id));
      return vt?.[field];
    });
  }, [vtransactions, releaseSettled]);

  // Overlay the values committed but not yet echoed back, so a later refetch
  // can't revert them.
  const heldVtransactions = useMemo(
    () =>
      (vtransactions ?? []).map((vt) => {
        const held = Object.keys(pendingEdits).filter((key) =>
          key.startsWith(`${vt.id}:`)
        );
        if (!held.length) return vt;
        const row = { ...vt } as Vtransaction_Entity;
        held.forEach((key) => {
          const field = key.slice(key.indexOf(":") + 1);
          row[field] = pendingValue(vt.id, field, row[field]);
        });
        return row;
      }),
    [vtransactions, pendingEdits, pendingValue]
  );

  const talentVtransactions =
    heldVtransactions.reduce((acc, vt) => {
      if (!acc[vt.talentID]) acc[vt.talentID] = [];
      acc[vt.talentID].push(vt);
      return acc;
    }, {} as { [talentID: number]: Vtransaction_Entity[] }) ?? {};

  const talentRows = talentVtransactions[_talentID!] ?? [];

  // Whoever owns the picker owns the range. With neither an external range nor
  // a picker of our own — the panel beside Jobs — nothing is filtered.
  const activeRange: DateRange<Moment> =
    dateRange ?? (withSummary ? date : [null, null]);

  // The grid lists what happened inside the range.
  const rows = talentRows.filter((vt) =>
    withinDateRange(vt.createdAt, activeRange)
  );

  // The balance is a position rather than a flow, so it counts everything up to
  // the end of the range and ignores the start. Netting only the transactions
  // inside the window reports 0 for anyone whose history predates it, which
  // reads as "settled" when it means "nothing happened lately".
  const balanceRows = talentRows.filter((vt) =>
    withinDateRange(vt.createdAt, [null, activeRange[1]])
  );

  const onVtransactionUpdate = async (
    _newVtransaction: Vtransaction_Entity,
    _oldVtransaction?: Vtransaction_Entity
  ) => {
    const editedFields = Object.keys(_newVtransaction).filter(
      (key) => _newVtransaction[key] !== _oldVtransaction?.[key]
    );

    // Hold the edited fields until the server reports them, so a later refetch
    // can't revert them. Not awaited: holding the cell in edit mode for a
    // round-trip swallows whatever is typed next.
    editedFields.forEach((field) =>
      markPending(_newVtransaction.id, field, _newVtransaction[field])
    );

    updateVtransaction({
      id: _newVtransaction.id,
      body: _newVtransaction,
    })
      .unwrap()
      .catch((error) => {
        editedFields.forEach((field) =>
          dropPending(_newVtransaction.id, field)
        );
        reportSaveError(error);
      });

    return { ..._newVtransaction };
  };

  const onVtransactionCreate = () => {
    if (_talentID) {
      const newVtransaction = {
        talentID: _talentID,
        projectID: _projectID,
        amount: 0,
        id: -1,
        notes: "",
        createdAt: "",
        projectName: "",
        updatedAt: "",
      };
      createVtransaction(newVtransaction);
    }
  };

  const dispatch = useDispatch();
  return (
    <Paper
      sx={{
        p: header ? 2 : 0,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {withSummary && (
        <LedgerSummary
          date={date}
          onDateChange={setDate}
          jobs={jobs ?? []}
          vtransactions={balanceRows}
        />
      )}
      <DataGridPro
        {...defaultDataGridProps}
        apiRef={apiRef}
        isCellEditable={(e) => {
          if (e.colDef.field === "amount") return e.row.projectID === 0;
          return e.colDef.editable ?? false;
        }}
        onRowClick={(e) => {
          confirm({
            description: `Do you want to open the project "${e.row.projectName}"?`,
          })
            .then(() => {
              dispatch(setSelectedProjectID(e.row.projectID));
              dispatch(setSelectedVtransactionTalentID(_talentID));
              dispatch(setSelectedTalentID());
              navigate(
                `/projects/${e.row.projectID}?tab=1&talentID=${_talentID}`
              );
            })
            .catch(() => {
              /* ... */
            });
        }}
        getRowClassName={(e) => {
          let className = "";
          if (e.row.projectID === _projectID) className += "highlightedRow";
          return className;
        }}
        processRowUpdate={onVtransactionUpdate}
        onProcessRowUpdateError={reportSaveError}
        rows={rows}
        columns={vtransactionColumns(dispatch, confirm, deleteVtransaction)}
        initialState={{}}
        showToolbar
        slots={{
          toolbar: VtransactionToolbar,
          noRowsOverlay: NoRowsOverlay,
          footer: TalentLedgerFooter,
        }}
        slotProps={{
          footer: { rows },
          toolbar: {
            projectID: _projectID,
            talentID: _talentID,
            onCreate: onVtransactionCreate,
            onClose: () => dispatch(setSelectedVtransactionTalentID()),
          },
          cell: { tabIndex: 1 },
        }}
      />
      {saveErrorSnackbar}
    </Paper>
  );
}

/**
 * Date range picker and the figures for that range: what the talent earned and
 * how long it took them, from their jobs, alongside where their ledger stands.
 *
 * @param {object} props Component props.
 * @param {Array} props.date Selected range.
 * @param {Function} props.onDateChange Called when the range changes.
 * @param {Array} props.jobs The talent's jobs, unfiltered.
 * @param {Array} props.vtransactions The talent's transactions up to the end of
 * the range — every one of them, not just those inside it.
 * @returns {ReactElement} The summary strip.
 */
function LedgerSummary({
  date,
  onDateChange,
  jobs,
  vtransactions,
}: {
  date: DateRange<Moment>;
  onDateChange: (d: DateRange<Moment>) => void;
  jobs: { hours: number; total: number; createdAt: string }[];
  vtransactions: Vtransaction_Entity[];
}) {
  const { total, totalHours, avgHourlyRate } = summarizeEarnings(
    jobs.filter((j) => withinDateRange(j.createdAt, date))
  );
  const { balance } = summarizeLedger(vtransactions);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, p: 1 }}>
      <DateRangePicker
        date={date}
        onChange={onDateChange}
        emptyLabel="All time"
      />
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        <SummaryCard label="Earned" value={currencyFormatter.format(total)} />
        <SummaryCard label="Total Hours" value={`${totalHours}`} />
        <SummaryCard
          label="Avg Hourly Rate"
          // No hours logged is no hourly basis, not a rate of nothing.
          value={totalHours ? currencyFormatter.format(avgHourlyRate) : "—"}
        />
        <SummaryCard
          label="Balance"
          value={currencyFormatter.format(balance / 10000)}
          caption={
            date[1] ? `as of ${date[1].format("ll")}` : "all transactions"
          }
        />
      </Box>
    </Box>
  );
}

/**
 * @param {object} props Component props.
 * @param {string} props.label Heading above the figure.
 * @param {string} props.value The figure.
 * @param {string} props.caption Optional note under the figure.
 * @returns {ReactElement} One card in the summary strip.
 */
function SummaryCard({
  label,
  value,
  caption,
}: {
  label: string;
  value: string;
  caption?: string;
}) {
  return (
    <Paper sx={{ p: 1, minWidth: 120 }}>
      <Typography variant="caption">{label}</Typography>
      <Typography variant="h6">
        <b>{value}</b>
      </Typography>
      {caption ? <Typography variant="caption">{caption}</Typography> : null}
    </Paper>
  );
}

function TalentLedgerFooter({ rows }: { rows: Vtransaction_Entity[] }) {
  let total = 0;

  for (const key in rows) {
    if (Object.prototype.hasOwnProperty.call(rows, key)) {
      const row = rows[key];
      total += row.amount;
    }
  }
  return (
    <>
      <Divider />
      <Box sx={{ p: 2, textAlign: "right", fontWeight: 600 }}>
        Total: {currencyFormatter.format(total / 10000)}
      </Box>
    </>
  );
}

function VtransactionToolbar({
  talentID,
  projectID,
  onCreate,
  onClose,
}: {
  talentID: number;
  projectID: number;
  onCreate: () => void;
  onClose: () => void;
}) {
  if (!talentID) return <div />;

  useHotkeys(`ctrl+enter`, onCreate, {
    enableOnTags: ["INPUT", "SELECT", "TEXTAREA"],
  });

  return (
    <Toolbar>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flex: 1,
          }}
        >
          <Tooltip title="ctrl + enter">
            <Button variant="soft" size="sm" onClick={onCreate}>
              + New Transaction
            </Button>
          </Tooltip>
          <QuickFilter
            expanded
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
          >
            <QuickFilterControl
              size="small"
              fullWidth
              placeholder="Search..."
            />
            <QuickFilterClear />
          </QuickFilter>
        </Box>
        {projectID ? (
          <Button
            startDecorator={<i className="fa fa-times pright" />}
            size="sm"
            onClick={onClose}
          >
            Close
          </Button>
        ) : (
          []
        )}
      </Box>
    </Toolbar>
  );
}

const vtransactionColumns = (
  dispatch: Dispatch,
  confirm: (options?: ConfirmOptions | undefined) => Promise<void>,
  deleteVtransaction: (id: number) => void
) =>
  injectDefaultColumnProps([
    {
      field: "id",
      headerName: "ID",
      type: "string",
      width: 40,
    },
    {
      field: "createdAt",
      headerName: "Date",
      editable: false,
      flex: 1,
      type: "string",
      valueFormatter: (valueOrParams: any) =>
        moment(valueOrParams?.value ?? valueOrParams).format("ll"),
    },
    {
      field: "projectName",
      headerName: "Project",
      editable: false,
      flex: 1,
      type: "string",
    },
    {
      field: "notes",
      headerName: "Notes",
      editable: true,
      flex: 1,
      type: "string",
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 100,
      editable: true,
      type: "number",
      valueGetter: getterDivider("amount", 10000),
      valueSetter: setterDivider("amount", 10000),
      valueFormatter: (valueOrParams: any) =>
        `${currencyFormatter.format(valueOrParams?.value ?? valueOrParams)}`,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      cellClassName: "actions",
      getActions: (e: any) => {
        if (e.row.projectID !== 0) {
          return [];
        }
        return [
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label={`Delete ${e.row.id}`}
            onClick={() => {
              confirm({
                description: `You are about to delete a virtual transaction`,
              })
                .then(() => {
                  deleteVtransaction(e.row.id);
                })
                .catch(() => {
                  /* ... */
                });
            }}
            color="inherit"
          />,
        ];
      },
    },
  ]);
